import { CSSResultGroup, html, LitElement, PropertyValues } from 'lit'
import { classMap } from 'lit/directives/class-map.js'
import { customElement, property, state, queryAssignedElements } from 'lit/decorators.js'
import { style } from './steps.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { CSEStep } from 'elements/main.js'

export const stepsVariants = ['default', 'vertical'] as const
export type stepsVariantsType = typeof stepsVariants

@customElement('cse-steps')
export class CSESteps extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, fontStyles, style]

  @property({ type: Number, attribute: 'active-index' })
  activeIndex = 0

  @property({ type: String })
  variant: stepsVariantsType[number] = 'default'

  @queryAssignedElements({ flatten: true })
  _steps!: Array<CSEStep>

  @state()
  _statusBar: number = 0

  protected override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)

    if (this._steps.length < 1) {
      throw new Error('cse-steps must contain at least 1 cse-step')
    }

    if (changedProperties.has('activeIndex')) {
      this.setStepStates()
    }

    if (changedProperties.has('variant')) {
      this.setStepVariants()
    }

    this._statusBar = this.calculateStatusBar()
  }

  // Adding variant to cse-step when cse-steps changes
  private setStepVariants() {
    this._steps.forEach((step: CSEStep) => {
      step.variant = this.variant
    })
  }

  // Adding active state to cse-step
  private setStepStates() {
    const isLast = this.activeIndex === this._steps.length

    this._steps.forEach((step: CSEStep, index: number) => {
      if (index <= this.activeIndex - 1 || isLast) {
        step.state = 'completed'
      }

      if (index === this.activeIndex - 1 && !isLast) {
        step.state = 'active'
      }

      if (index > this.activeIndex - 1) {
        step.state = 'initial'
      }
    })
  }

  // Calcule the percenatge of the status bar
  private calculateStatusBar() {
    const unit: number = 100 / (this._steps.length - 1)
    const percentage = this.activeIndex >= this._steps.length ? 100 : (this.activeIndex - 1) * unit
    return percentage
  }

  override render() {
    return html`
      <div style="--percentage: ${this._statusBar}%" class= ${classMap({
        'cse-steps': true,
        [`variant-${this.variant}`]: this.variant === 'vertical'
      })}>
        <slot></slot>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-steps': CSESteps
  }
}
